import React, { useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { isObj, type FormContent, type FormFieldContent } from './shared';
import type { ArtifactFormSubmission, ArtifactFormSubmissionValue } from '@easyflow/shared';
import type { ArtifactViewerProps } from '../types';

// Form artefatto con VERO invio dei valori al PA (Task #3817): alla submit i
// valori tipizzati (string/number/boolean) partono come `form_submission` del
// turno successivo via contratto Zod condiviso (`ArtifactFormSubmissionSchema`).
// Fallback legacy: senza `onFormSubmit` resta il vecchio onAction(toolName).

function initialValues(fields: FormFieldContent[]): Record<string, ArtifactFormSubmissionValue> {
  const out: Record<string, ArtifactFormSubmissionValue> = {};
  for (const f of fields) {
    if (f.type === 'checkbox') out[f.name] = f.defaultValue === true;
    else if (f.type === 'number') out[f.name] = typeof f.defaultValue === 'number' ? f.defaultValue : '';
    else out[f.name] = typeof f.defaultValue === 'string' || typeof f.defaultValue === 'number' ? String(f.defaultValue) : '';
  }
  return out;
}

const inputClass = 'px-2 py-1.5 text-sm border border-border rounded focus:border-violet-500 focus:ring-1 focus:ring-inset focus:ring-violet-500 outline-none';

export const FormViewer: React.FC<ArtifactViewerProps> = ({ artifact, onAction, onFormSubmit }) => {
  const c = artifact.content as Partial<FormContent>;
  const fields = useMemo(
    () => (Array.isArray(c?.fields) ? c.fields : []).filter((f): f is FormFieldContent => isObj(f) && typeof f.name === 'string'),
    [c?.fields],
  );
  const submitLabel = typeof c?.submitLabel === 'string' ? c.submitLabel : 'Invia';
  const toolName = typeof c?.toolName === 'string' ? c.toolName : undefined;

  const [values, setValues] = useState<Record<string, ArtifactFormSubmissionValue>>(() => initialValues(fields));
  const [sent, setSent] = useState(false);

  if (fields.length === 0) return <div className="p-4 text-sm text-muted-foreground">Form senza campi</div>;

  const setValue = (name: string, v: ArtifactFormSubmissionValue): void => {
    setSent(false);
    setValues(prev => ({ ...prev, [name]: v }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (onFormSubmit) {
      const out: Record<string, ArtifactFormSubmissionValue> = {};
      for (const f of fields) {
        const raw = values[f.name];
        if (f.type === 'checkbox') out[f.name] = raw === true;
        else if (f.type === 'number') {
          const n = typeof raw === 'number' ? raw : Number(String(raw ?? ''));
          out[f.name] = Number.isFinite(n) && String(raw ?? '').trim() !== '' ? n : String(raw ?? '');
        } else out[f.name] = typeof raw === 'string' ? raw : String(raw ?? '');
      }
      const submission: ArtifactFormSubmission = {
        artifactId: artifact.id,
        slug: artifact.slug,
        title: artifact.title,
        ...(toolName ? { toolName } : {}),
        values: out,
      };
      onFormSubmit(submission);
      setSent(true);
      return;
    }
    if (toolName) onAction?.(toolName);
  };

  return (
    <form className="bg-card rounded-lg border border-border p-4 space-y-3" onSubmit={handleSubmit}>
      {fields.map((f) => (
        <div key={f.name} className="flex flex-col gap-1">
          <label htmlFor={`paf-${artifact.id}-${f.name}`} className="text-xs font-medium text-foreground">
            {f.label}{f.required ? ' *' : ''}
          </label>
          {f.type === 'textarea' ? (
            <textarea
              id={`paf-${artifact.id}-${f.name}`}
              name={f.name}
              required={f.required}
              value={typeof values[f.name] === 'string' ? values[f.name] as string : String(values[f.name] ?? '')}
              onChange={(e) => setValue(f.name, e.target.value)}
              className={`${inputClass} min-h-[80px]`}
            />
          ) : f.type === 'select' ? (
            <select
              id={`paf-${artifact.id}-${f.name}`}
              name={f.name}
              required={f.required}
              value={typeof values[f.name] === 'string' ? values[f.name] as string : ''}
              onChange={(e) => setValue(f.name, e.target.value)}
              className={inputClass}
            >
              <option value="">—</option>
              {(f.options ?? []).map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          ) : f.type === 'checkbox' ? (
            <input
              id={`paf-${artifact.id}-${f.name}`}
              type="checkbox"
              name={f.name}
              checked={values[f.name] === true}
              onChange={(e) => setValue(f.name, e.target.checked)}
              className="h-4 w-4 self-start"
            />
          ) : (
            <input
              id={`paf-${artifact.id}-${f.name}`}
              type={f.type === 'number' ? 'number' : f.type}
              name={f.name}
              required={f.required}
              value={typeof values[f.name] === 'number' ? String(values[f.name]) : (values[f.name] as string ?? '')}
              onChange={(e) => setValue(f.name, f.type === 'number' && e.target.value !== '' ? Number(e.target.value) : e.target.value)}
              className={inputClass}
            />
          )}
        </div>
      ))}
      <div className="pt-1 flex items-center gap-2">
        <Button type="submit" size="sm">{submitLabel}</Button>
        {sent && (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-600 animate-in fade-in-0" data-testid="form-sent">
            <Check className="h-3.5 w-3.5" /> Inviato all'assistente
          </span>
        )}
        {!sent && toolName && <span className="text-xs text-muted-foreground">→ <code>{toolName}</code></span>}
      </div>
    </form>
  );
};
